import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { Product } from "@/data/products";
import LazyImage from "@/components/LazyImage";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useWishlist } from "@/context/WishlistContext";

interface ProductCardProps {
  product: Product;
  index?: number;
}

const ProductCard = ({ product, index = 0 }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { t } = useLanguage();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const wishlisted = isInWishlist(product.id);

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultSize = product.sizes[0] || { label: "Default", price: product.price };
    addToCart(product, defaultSize.label, defaultSize.price);
  };

  const displayPrice = product.sizes[0]?.price || product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Link to={`/product/${product.id}`} className="block group rounded-xl">
        <div className="luxury-card rounded-sm overflow-hidden">
          <div className="product-image-hover aspect-product relative overflow-hidden">
            <LazyImage src={product.image || "/placeholder.svg"} alt={product.name} className="w-full h-full rounded-md" />
            <button
              onClick={handleWishlistToggle}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
            >
              <Heart className={`w-4 h-4 transition-colors ${wishlisted ? "fill-primary text-primary" : "text-foreground/70"}`} />
            </button>
            {!product.inStock && (
              <div className="absolute top-4 left-4 px-3 py-1 bg-destructive/90 text-destructive-foreground text-xs uppercase tracking-wider rounded-sm">
                {t.productDetail.outOfStock}
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 dark:from-background/90 dark:via-background/20 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            {product.inStock && (
              <button onClick={handleQuickAdd} className="absolute bottom-4 left-4 right-4 z-10 py-3 bg-primary text-primary-foreground text-sm font-medium tracking-wider uppercase opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 rounded-sm hover:bg-primary/90 shadow-lg">
                {t.productCard.addToCart} — {displayPrice} {t.common.egp}
              </button>
            )}
          </div>

          <div className="p-5 light:bg-gradient-to-b light:from-white/80 light:to-white">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-serif text-xl transition-colors text-primary">{product.name}</h3>
                {product.nameArabic && <p className="text-sm mt-1 arabic text-primary">{product.nameArabic}</p>}
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className="text-primary font-medium">{product.price} {t.common.egp}</span>
                {product.categories && product.categories.length > 1 ? (
                  <div className="flex flex-wrap gap-1 mt-1 justify-end">
                    {product.categories.map(cat => (
                      <span key={cat} className="text-[10px] tracking-wider uppercase text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded">
                        {cat}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs tracking-wider uppercase text-muted-foreground mt-1">{product.category}</span>
                )}
              </div>
            </div>
            {product.story && (
              <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2 mt-3" dir="rtl">{product.story}</p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
